/**
 * LeftShield Portal - AI Constitution Rules Page
 * View and manage the AI constitution rules that govern LeftShield's AI behavior.
 * Route: /ai-governance/constitution | Category: AI
 */
import React from 'react';
import {
  Title2,
  Title3,
  Body1,
  Body2,
  Caption1,
  Card,
  Badge,
  Button,
  tokens,
  makeStyles,
} from '@fluentui/react-components';
import {
  GavelRegular,
  ShieldCheckmarkRegular,
  ArrowLeftRegular,
  CheckmarkCircleRegular,
} from '@fluentui/react-icons';
import { useNavigate } from 'react-router-dom';
import { usePageStyles } from '../styles/page-styles';
import { PageHeader } from '../components/common/PageHeader';
import { governanceTokens } from '../components/design-system';

// ---------------------------------------------------------------------------
// Local styles
// ---------------------------------------------------------------------------

const AI_COLOR = '#8852E0';

const useLocalStyles = makeStyles({
  ruleCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalL,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  ruleHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalM,
  },
  ruleTitle: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    flex: 1,
  },
  ruleMeta: {
    display: 'flex',
    gap: tokens.spacingHorizontalL,
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  toggleVisual: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusSmall,
    cursor: 'default',
  },
  tagRow: {
    display: 'flex',
    gap: tokens.spacingHorizontalXS,
    flexWrap: 'wrap',
  },
});

// ---------------------------------------------------------------------------
// Demo data
// ---------------------------------------------------------------------------

interface ConstitutionRule {
  id: string;
  code: string;
  title: string;
  description: string;
  severity: 'Critical' | 'High' | 'Medium' | 'Low';
  appliesTo: string[];
  enabled: boolean;
  lastTriggered: string | null;
  triggerCount: number;
}

const CONSTITUTION_RULES: ConstitutionRule[] = [
  {
    id: 'r1',
    code: 'AI_NO_DIRECT_ENFORCEMENT',
    title: 'No Direct Enforcement',
    description: 'AI cannot trigger enforce, block, or approve actions. All enforcement must be initiated by a human or a pre-approved automation rule.',
    severity: 'Critical',
    appliesTo: ['RiskScoringAgent', 'PolicyEngine', 'ComplianceAdvisor', 'FixPRAgent'],
    enabled: true,
    lastTriggered: '2026-03-08T09:14:00Z',
    triggerCount: 12,
  },
  {
    id: 'r2',
    code: 'AI_NO_EVIDENCE_FABRICATION',
    title: 'No Evidence Fabrication',
    description: 'AI cannot create evidence objects. Evidence must originate from scanners, integrations, or verified external sources.',
    severity: 'Critical',
    appliesTo: ['All Agents'],
    enabled: true,
    lastTriggered: '2026-03-07T16:42:00Z',
    triggerCount: 3,
  },
  {
    id: 'r3',
    code: 'AI_COMPLIANCE_CLAIM_REQUIRES_EVIDENCE_LINK',
    title: 'Compliance Claims Require Evidence',
    description: 'Any AI-generated compliance claim must include at least one supporting evidence reference that can be independently verified.',
    severity: 'High',
    appliesTo: ['ComplianceAdvisor', 'PolicyEngine', 'RiskScoringAgent'],
    enabled: true,
    lastTriggered: '2026-03-09T11:28:00Z',
    triggerCount: 847,
  },
  {
    id: 'r4',
    code: 'AI_HIGH_RISK_REQUIRES_VALIDATION',
    title: 'High Risk Requires Validation',
    description: 'Any AI output classified as high or critical risk must pass through the validation agent before being surfaced to users.',
    severity: 'High',
    appliesTo: ['RiskScoringAgent', 'PolicyEngine'],
    enabled: true,
    lastTriggered: '2026-03-09T14:32:00Z',
    triggerCount: 312,
  },
  {
    id: 'r5',
    code: 'AI_POLICY_CHANGE_REQUIRES_SIMULATION',
    title: 'Policy Changes Require Simulation',
    description: 'AI-suggested policy changes must go through the simulation engine to assess impact before being presented for approval.',
    severity: 'High',
    appliesTo: ['PolicyEngine', 'ComplianceAdvisor'],
    enabled: true,
    lastTriggered: '2026-03-09T08:15:00Z',
    triggerCount: 58,
  },
  {
    id: 'r6',
    code: 'AI_EXCEPTION_APPROVAL_REQUIRES_HUMAN',
    title: 'Exception Approval Requires Human',
    description: 'AI may suggest exceptions to policies but cannot approve them. All exception grants require explicit human approval.',
    severity: 'Medium',
    appliesTo: ['PolicyEngine', 'ComplianceAdvisor'],
    enabled: true,
    lastTriggered: '2026-03-06T14:09:00Z',
    triggerCount: 19,
  },
  {
    id: 'r7',
    code: 'AI_UNSUPPORTED_OUTPUT_REQUIRES_REVIEW',
    title: 'Unsupported Output Requires Review',
    description: 'Any AI output that cannot be linked to supporting evidence must be flagged for human review and cannot be auto-actioned.',
    severity: 'Medium',
    appliesTo: ['All Agents'],
    enabled: true,
    lastTriggered: '2026-03-09T13:47:00Z',
    triggerCount: 34,
  },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const severityColor = (severity: string): 'danger' | 'warning' | 'important' | 'informative' => {
  switch (severity) {
    case 'Critical': return 'danger';
    case 'High': return 'warning';
    case 'Medium': return 'important';
    default: return 'informative';
  }
};

const formatDate = (iso: string | null): string => {
  if (!iso) return 'Never';
  return new Date(iso).toLocaleString();
};

// ---------------------------------------------------------------------------
// Summary stats
// ---------------------------------------------------------------------------

const totalRules = CONSTITUTION_RULES.length;
const criticalRules = CONSTITUTION_RULES.filter((r) => r.severity === 'Critical').length;
const highRules = CONSTITUTION_RULES.filter((r) => r.severity === 'High').length;
const mediumRules = CONSTITUTION_RULES.filter((r) => r.severity === 'Medium').length;
const totalTriggers = CONSTITUTION_RULES.reduce((sum, r) => sum + r.triggerCount, 0);

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const AIConstitutionRulesPage: React.FC = () => {
  const ps = usePageStyles();
  const ls = useLocalStyles();
  const navigate = useNavigate();

  return (
    <div className={ps.page}>
      <PageHeader
        title="AI Constitution Rules"
        subtitle="The immutable rules that govern LeftShield's AI behavior. These rules cannot be overridden by AI agents."
        icon={<GavelRegular />}
        iconColor={AI_COLOR}
        actions={
          <Button
            appearance="subtle"
            icon={<ArrowLeftRegular />}
            onClick={() => navigate('/ai-governance')}
          >
            Back to AI Governance
          </Button>
        }
      />

      {/* Summary Stats */}
      <Card>
        <div className={ps.cardBody}>
          <div className={ps.sectionHeader}>
            <Title2>Constitution Summary</Title2>
            <Badge appearance="outline" color="informative" icon={<ShieldCheckmarkRegular />}>
              All rules active
            </Badge>
          </div>

          <div className={ps.statGrid}>
            <div className={ps.statTile}>
              <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Total Rules</Body2>
              <Title2 style={{ color: AI_COLOR }}>{totalRules}</Title2>
            </div>
            <div className={ps.statTile}>
              <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Critical</Body2>
              <Title2 style={{ color: governanceTokens.colorStatusError }}>{criticalRules}</Title2>
            </div>
            <div className={ps.statTile}>
              <Body2 style={{ color: tokens.colorNeutralForeground3 }}>High</Body2>
              <Title2 style={{ color: '#E89652' }}>{highRules}</Title2>
            </div>
            <div className={ps.statTile}>
              <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Medium</Body2>
              <Title2 style={{ color: governanceTokens.colorStatusWarning }}>{mediumRules}</Title2>
            </div>
            <div className={ps.statTile}>
              <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Total Triggers</Body2>
              <Title2 className={ps.metricValue}>{totalTriggers.toLocaleString()}</Title2>
            </div>
          </div>
        </div>
      </Card>

      {/* Rules List */}
      <Card>
        <div className={ps.cardBody}>
          <div className={ps.sectionHeader}>
            <Title2>Constitution Rules</Title2>
            <Badge appearance="outline" color="brand">
              {CONSTITUTION_RULES.length} rules
            </Badge>
          </div>

          <div className={ps.rowList}>
            {CONSTITUTION_RULES.map((rule) => (
              <div key={rule.id} className={ls.ruleCard}>
                <div className={ls.ruleHeader}>
                  <div className={ls.ruleTitle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS }}>
                      <Body1 className={ps.metricValue} style={{ fontWeight: 600, color: AI_COLOR }}>
                        {rule.code}
                      </Body1>
                      <Badge appearance="tint" color={severityColor(rule.severity)} size="small">
                        {rule.severity}
                      </Badge>
                    </div>
                    <Title3>{rule.title}</Title3>
                    <Body2 style={{ color: tokens.colorNeutralForeground3 }}>{rule.description}</Body2>
                  </div>

                  <div
                    className={ls.toggleVisual}
                    style={{
                      backgroundColor: rule.enabled
                        ? governanceTokens.colorStatusSuccess + '20'
                        : tokens.colorNeutralBackground1,
                    }}
                  >
                    <CheckmarkCircleRegular
                      style={{
                        color: rule.enabled
                          ? governanceTokens.colorStatusSuccess
                          : tokens.colorNeutralForeground3,
                      }}
                    />
                    <Caption1 style={{
                      color: rule.enabled
                        ? governanceTokens.colorStatusSuccess
                        : tokens.colorNeutralForeground3,
                      fontWeight: 600,
                    }}>
                      {rule.enabled ? 'Enabled' : 'Disabled'}
                    </Caption1>
                  </div>
                </div>

                <div className={ls.tagRow}>
                  <Caption1 style={{ color: tokens.colorNeutralForeground3, marginRight: tokens.spacingHorizontalXS }}>
                    Applies to:
                  </Caption1>
                  {rule.appliesTo.map((tag) => (
                    <Badge key={tag} appearance="outline" size="small">{tag}</Badge>
                  ))}
                </div>

                <div className={ls.ruleMeta}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalXS }}>
                    <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Last triggered:</Caption1>
                    <Body2>{formatDate(rule.lastTriggered)}</Body2>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalXS }}>
                    <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Trigger count:</Caption1>
                    <Body2 className={ps.metricValue} style={{ fontWeight: 600 }}>
                      {rule.triggerCount.toLocaleString()}
                    </Body2>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Card>
    </div>
  );
};
